import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getDocument } from '../utils/matchHelpers';
import { useAuth } from '../context/AuthContext';
import MatchTimer from '../components/MatchTimer';
import Footer from '../components/Footer';

export default function Welcome() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [expandedTournament, setExpandedTournament] = useState(null);
  const [tournamentData, setTournamentData] = useState({});
  const [tournamentLoading, setTournamentLoading] = useState({});

  useEffect(() => {
    if (admin) { navigate('/dashboard'); return; }

    // Load tournaments
    getDocs(collection(db, 'tournaments')).then(snap => {
      setTournaments(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(t => t.status !== 'setup')
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
      );
    });

    // Live matches via onSnapshot
    const q = query(collection(db, 'matches'), where('status', '==', 'active'));
    const unsub = onSnapshot(q, async (snap) => {
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const enriched = await Promise.all(raw.map(async m => {
        const [team1, team2] = await Promise.all([
          getDocument('teams', m.team1_id),
          getDocument('teams', m.team2_id),
        ]);
        return {
          id: m.id,
          team1_id: m.team1_id, team2_id: m.team2_id,
          team1_score: m.team1_score, team2_score: m.team2_score,
          current_server_team: m.current_server_team,
          court_number: m.court_number, started_at: m.started_at || null,
          team1_name: team1?.name || null, team2_name: team2?.name || null,
          tournament_name: null,
        };
      }));
      setLiveMatches(enriched);
      setLoading(false);
    });

    return unsub;
  }, [admin, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await login(loginForm.username, loginForm.password);
      navigate('/dashboard');
    } catch {
      setLoginError('Invalid email or password');
    } finally { setLoginLoading(false); }
  };

  const loadTournament = async (id) => {
    if (expandedTournament === id) { setExpandedTournament(null); return; }
    setExpandedTournament(id);
    if (tournamentData[id]) return;
    setTournamentLoading(p => ({ ...p, [id]: true }));
    try {
      const matchSnap = await getDocs(query(collection(db, 'matches'), where('tournament_id', '==', id)));
      const matches = matchSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Enrich with team names
      const teamIds = [...new Set(matches.flatMap(m => [m.team1_id, m.team2_id].filter(Boolean)))];
      const teamMap = {};
      await Promise.all(teamIds.map(async tid => {
        const t = await getDocument('teams', tid);
        if (t) teamMap[tid] = t;
      }));

      const enriched = matches.map(m => ({
        ...m,
        team1_name: teamMap[m.team1_id]?.name || null,
        team2_name: teamMap[m.team2_id]?.name || null,
      })).sort((a, b) => (a.round || 0) - (b.round || 0) || (a.match_order || 0) - (b.match_order || 0));

      setTournamentData(p => ({ ...p, [id]: { matches: enriched } }));
    } finally { setTournamentLoading(p => ({ ...p, [id]: false })); }
  };

  const activeTournaments = tournaments.filter(t => t.status === 'active');
  const completedTournaments = tournaments.filter(t => t.status === 'completed');

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0f1923 0%, #1a2332 55%, #0d2b1f 100%)', color: '#fff', fontFamily: 'inherit' }}>

      {/* Top Nav */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(15,25,35,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 20px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '22px' }}>🏓</span>
            <span style={{ fontSize: '18px', fontWeight: '900', color: '#27ae60', letterSpacing: '0.5px' }}>Live</span>
            <span style={{ fontSize: '18px', fontWeight: '900', color: '#fff' }}>Pickle Ball</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
            {!isMobile && (
              <>
                <Link to="/live-scores" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', padding: '6px 12px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
                  <span style={{ color: '#e74c3c', fontSize: '8px' }}>●</span> Live Scores
                </Link>
                <Link to="/leaderboard" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', padding: '6px 12px', borderRadius: '6px', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
                  📊 Leaderboard
                </Link>
              </>
            )}
            <button onClick={() => setShowLogin(v => !v)}
              style={{ padding: isMobile ? '8px 14px' : '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', background: showLogin ? 'rgba(39,174,96,0.3)' : '#27ae60', color: '#fff', border: '1px solid #27ae60', transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {isMobile ? '🔐 Login' : '🔐 Admin Login'}
            </button>
          </div>
        </div>

        {showLogin && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '16px 20px', background: 'rgba(0,0,0,0.3)' }}>
            <div style={{ maxWidth: '420px', margin: '0 auto' }}>
              <form onSubmit={handleLogin} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</div>
                  <input value={loginForm.username} onChange={e => setLoginForm(f => ({ ...f, username: e.target.value }))} required
                    placeholder="admin@example.com"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</div>
                  <input type="password" value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} required
                    placeholder="••••••••"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <button type="submit" disabled={loginLoading}
                  style={{ padding: '9px 20px', borderRadius: '7px', background: '#27ae60', color: '#fff', border: 'none', fontWeight: '700', fontSize: '14px', cursor: loginLoading ? 'not-allowed' : 'pointer', opacity: loginLoading ? 0.7 : 1, flexShrink: 0 }}>
                  {loginLoading ? '...' : 'Login'}
                </button>
              </form>
              {loginError && <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '8px', fontWeight: '600' }}>⚠ {loginError}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '52px 20px 40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏓</div>
        <h1 style={{ fontSize: 'clamp(26px, 6vw, 42px)', fontWeight: '900', margin: '0 0 10px', lineHeight: 1.1 }}>
          <span style={{ color: '#27ae60' }}>Pickle</span>Score
        </h1>
        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.45)', maxWidth: '420px', margin: '0 auto 20px', lineHeight: 1.6 }}>
          Live Pickle Ball scores, tournament brackets &amp; player standings
        </p>
        {liveMatches.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', color: '#27ae60', marginBottom: '16px' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'currentColor', animation: 'pulse 1.5s infinite' }} />
            Live updates active
          </div>
        )}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '8px' }}>
            <Link to="/live-scores" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', padding: '7px 16px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ color: '#e74c3c', fontSize: '8px' }}>●</span> Live Scores
            </Link>
            <Link to="/leaderboard" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', padding: '7px 16px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.15)' }}>
              📊 Leaderboard
            </Link>
          </div>
        )}
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 12px 48px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
        ) : (
          <>
            <Section title="🔴 Live Right Now" count={liveMatches.length} accent="#e74c3c">
              {liveMatches.length === 0 ? (
                <EmptyState icon="🏓" text="No matches live right now" sub="Check back soon" />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                  {liveMatches.map(m => <LiveMatchCard key={m.id} match={m} />)}
                </div>
              )}
            </Section>

            <Section title="🏆 Active Tournaments" count={activeTournaments.length} accent="#27ae60">
              {activeTournaments.length === 0 ? (
                <EmptyState icon="🏆" text="No active tournaments" sub="Tournaments will appear here once started" />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {activeTournaments.map(t => (
                    <TournamentAccordion key={t.id} tournament={t}
                      expanded={expandedTournament === t.id}
                      onToggle={() => loadTournament(t.id)}
                      data={tournamentData[t.id]}
                      dataLoading={tournamentLoading[t.id]} />
                  ))}
                </div>
              )}
            </Section>

            {completedTournaments.length > 0 && (
              <Section title="✅ Completed Tournaments" count={completedTournaments.length} accent="#2980b9">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {completedTournaments.map(t => (
                    <TournamentAccordion key={t.id} tournament={t}
                      expanded={expandedTournament === t.id}
                      onToggle={() => loadTournament(t.id)}
                      data={tournamentData[t.id]}
                      dataLoading={tournamentLoading[t.id]} />
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>

      <Footer dark />
    </div>
  );
}

function Section({ title, count, accent, children }) {
  return (
    <div style={{ marginBottom: '36px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <h2 style={{ fontSize: '17px', fontWeight: '800', color: '#fff', margin: 0 }}>{title}</h2>
        {count > 0 && (
          <span style={{ fontSize: '12px', fontWeight: '700', color: accent, background: `${accent}22`, padding: '2px 9px', borderRadius: '10px' }}>
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ icon, text, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 20px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.08)' }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>{text}</div>
      {sub && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.2)' }}>{sub}</div>}
    </div>
  );
}

function LiveMatchCard({ match: m }) {
  const isT1Serving = m.current_server_team === m.team1_id;
  const isT1Leading = m.team1_score > m.team2_score;
  const isT2Leading = m.team2_score > m.team1_score;

  return (
    <Link to={`/matches/${m.id}/live`} style={{ textDecoration: 'none' }}>
      <div style={{ background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: '12px', padding: '14px 16px', cursor: 'pointer', transition: 'all 0.2s' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.12)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.4)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.06)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.2)'; }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
            {m.tournament_name || 'Standalone'} · Court {m.court_number}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {m.started_at && (
              <MatchTimer startedAt={m.started_at} compact
                style={{ fontSize: '11px', fontWeight: '700', color: '#f39c12', background: 'rgba(243,156,18,0.12)', padding: '2px 6px', borderRadius: '5px' }} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '700', color: '#e74c3c' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#e74c3c', animation: 'pulse 1s infinite' }} />
              LIVE
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {isT1Serving && <span style={{ color: '#27ae60', fontSize: '8px' }}>●</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{m.team1_name}</span>
            </div>
            <div style={{ fontSize: '44px', fontWeight: '900', lineHeight: 1, color: isT1Leading ? '#2ecc71' : 'rgba(255,255,255,0.85)' }}>
              {m.team1_score}
            </div>
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'rgba(255,255,255,0.2)', paddingBottom: '4px' }}>–</div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {!isT1Serving && <span style={{ color: '#27ae60', fontSize: '8px' }}>●</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{m.team2_name}</span>
            </div>
            <div style={{ fontSize: '44px', fontWeight: '900', lineHeight: 1, color: isT2Leading ? '#2ecc71' : 'rgba(255,255,255,0.85)' }}>
              {m.team2_score}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '8px' }}>Tap to watch live →</div>
      </div>
    </Link>
  );
}

function TournamentAccordion({ tournament: t, expanded, onToggle, data, dataLoading }) {
  const statusColor = { setup: '#f39c12', active: '#27ae60', completed: '#2980b9' };
  const color = statusColor[t.status] || '#7f8c8d';

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${expanded ? color + '44' : 'rgba(255,255,255,0.08)'}`, borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.2s' }}>
      <button onClick={onToggle} style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '20px', flexShrink: 0 }}>🏆</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
              {t.type} · {t.format?.replace('_', ' ')} · to {t.points_to_win} pts
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color, background: color + '22', padding: '3px 10px', borderRadius: '8px' }}>{t.status}</span>
          {t.status === 'completed' && (
            <Link to={`/tournament-results/${t.id}`}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: '11px', fontWeight: '700', color: '#f39c12', background: 'rgba(243,156,18,0.12)', border: '1px solid rgba(243,156,18,0.3)', padding: '3px 10px', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              🏅 Results
            </Link>
          )}
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.3)', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
        </div>
      </button>

      {expanded && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '14px 16px' }}>
          {dataLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>Loading...</div>
          ) : data ? (
            <>
              {data.matches && data.matches.length > 0 ? (
                <MatchList matches={data.matches} />
              ) : (
                <div style={{ textAlign: 'center', padding: '16px', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>No matches yet</div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function MatchList({ matches }) {
  const statusIcon = { scheduled: '⏳', setup: '⚙️', active: '🔴', completed: '✅' };
  const byRound = matches.reduce((acc, m) => {
    const r = m.round || 1;
    if (!acc[r]) acc[r] = [];
    acc[r].push(m);
    return acc;
  }, {});

  return (
    <div>
      {Object.entries(byRound).sort(([a], [b]) => +a - +b).map(([round, rMatches]) => (
        <div key={round} style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            Round {round}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {rMatches.map(m => {
              const isLive = m.status === 'active';
              const isDone = m.status === 'completed';
              const isT1Win = m.winner_team_id === m.team1_id;

              let sets = [];
              if (isDone && m.set_scores) {
                try { sets = JSON.parse(m.set_scores); } catch {}
              }
              const hasSets = sets.length > 0;
              const t1Sets = hasSets ? (m.team1_sets ?? sets.filter(s => Number(s.t1) > Number(s.t2)).length) : null;
              const t2Sets = hasSets ? (m.team2_sets ?? sets.filter(s => Number(s.t2) > Number(s.t1)).length) : null;

              return (
                <Link key={m.id} to={`/matches/${m.id}/live`} style={{ textDecoration: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', background: isLive ? 'rgba(231,76,60,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isLive ? 'rgba(231,76,60,0.25)' : 'rgba(255,255,255,0.06)'}`, cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(39,174,96,0.35)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = isLive ? 'rgba(231,76,60,0.25)' : 'rgba(255,255,255,0.06)'}>
                    <span style={{ fontSize: '13px', flexShrink: 0 }}>{statusIcon[m.status] || '⏳'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: isDone && isT1Win ? '800' : '600', color: isDone && isT1Win ? '#2ecc71' : '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isDone && isT1Win && '🏆 '}{m.team1_name}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'center' }}>
                      {hasSets ? (
                        <div>
                          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2px' }}>
                            {sets.map((s, i) => {
                              const s1 = Number(s.t1), s2 = Number(s.t2);
                              const sw = s1 > s2 ? 1 : s2 > s1 ? 2 : 0;
                              return (
                                <span key={i} style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  <span style={{ color: sw === 1 ? '#2ecc71' : '#e74c3c', fontWeight: '700' }}>{s.t1}</span>
                                  <span style={{ color: 'rgba(255,255,255,0.25)', margin: '0 1px' }}>-</span>
                                  <span style={{ color: sw === 2 ? '#2ecc71' : '#e74c3c', fontWeight: '700' }}>{s.t2}</span>
                                  {i < sets.length - 1 && <span style={{ color: 'rgba(255,255,255,0.2)', marginLeft: '2px' }}>,</span>}
                                </span>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: 'rgba(255,255,255,0.35)' }}>
                            <span style={{ color: isT1Win ? '#2ecc71' : 'rgba(255,255,255,0.55)' }}>{t1Sets}</span>
                            <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 2px' }}>–</span>
                            <span style={{ color: !isT1Win && m.winner_team_id ? '#2ecc71' : 'rgba(255,255,255,0.55)' }}>{t2Sets}</span>
                            {' sets'}
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '18px', fontWeight: '900', color: isDone && isT1Win ? '#2ecc71' : 'rgba(255,255,255,0.8)', minWidth: '22px', textAlign: 'center' }}>{m.team1_score}</span>
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)' }}>:</span>
                          <span style={{ fontSize: '18px', fontWeight: '900', color: isDone && !isT1Win && m.winner_team_id ? '#2ecc71' : 'rgba(255,255,255,0.8)', minWidth: '22px', textAlign: 'center' }}>{m.team2_score}</span>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '13px', fontWeight: isDone && !isT1Win && m.winner_team_id ? '800' : '600', color: isDone && !isT1Win && m.winner_team_id ? '#2ecc71' : '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isDone && !isT1Win && m.winner_team_id && '🏆 '}{m.team2_name}
                      </div>
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>C{m.court_number}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
